import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PageHeader from './Navigation/PageHeader';
import { motion } from 'framer-motion';
import * as workflowService from '../services/workflowService';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { checkWorkflowDependencies } from '../services/dependencyService';
import { useAuth } from './Auth/AuthProvider';

const {
  FiSettings,
  FiUsers,
  FiUserCheck,
  FiTrendingUp,
  FiPlay,
  FiPause,
  FiCheck,
  FiClock,
  FiAlertTriangle,
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiCalendar,
  FiTarget,
  FiBarChart3,
  FiRefreshCw,
  FiGrid,
  FiList,
  FiChevronLeft,
  FiChevronRight,
  FiEye
} = FiIcons;

const CompanyWorkflows = () => {
  const { isAdmin } = useAuth();
  const [activeWorkflows, setActiveWorkflows] = useState([]);
  const [selectedWorkflowType, setSelectedWorkflowType] = useState('');
  const [showNewWorkflowForm, setShowNewWorkflowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [workflowDependencies, setWorkflowDependencies] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState(null);

  // Company-level workflow templates
  const workflowTemplates = {
    recruitment: {
      name: 'Recruitment Campaign',
      icon: FiUsers,
      color: 'blue',
      description: 'Manage recruitment for specific positions or departments',
      defaultSteps: [
        { name: 'Job Analysis & Requirements', duration: 3, critical: true },
        { name: 'Job Advertisement Creation', duration: 2, critical: false },
        { name: 'Multi-Channel Posting', duration: 1, critical: false },
        { name: 'Application Collection Period', duration: 14, critical: false },
        { name: 'Initial Screening', duration: 5, critical: true },
        { name: 'Interview Process', duration: 7, critical: true },
        { name: 'Reference & Background Checks', duration: 5, critical: true },
        { name: 'Job Offer & Contract', duration: 3, critical: true }
      ]
    },
    onboarding: {
      name: 'Department Onboarding Program',
      icon: FiUserCheck,
      color: 'green',
      description: 'Structured onboarding program for new team members',
      defaultSteps: [
        { name: 'Pre-Start Preparation', duration: 7, critical: true },
        { name: 'Day One Welcome & Setup', duration: 1, critical: true },
        { name: 'First Week Integration', duration: 5, critical: true },
        { name: 'Month One Check-ins', duration: 20, critical: false },
        { name: 'Probation Review Process', duration: 60, critical: true },
        { name: 'Long-term Integration', duration: 90, critical: false }
      ]
    },
    performance: {
      name: 'Performance Review Cycle',
      icon: FiTrendingUp,
      color: 'purple',
      description: 'Company-wide performance management cycles',
      defaultSteps: [
        { name: 'Objective Setting Phase', duration: 14, critical: true },
        { name: 'Quarterly Check-in 1', duration: 90, critical: false },
        { name: 'Mid-Year Review', duration: 180, critical: true },
        { name: 'Quarterly Check-in 2', duration: 90, critical: false },
        { name: 'Annual Review Process', duration: 21, critical: true },
        { name: 'Development Planning', duration: 14, critical: false }
      ]
    },
    training: {
      name: 'Training Program Rollout',
      icon: FiTarget,
      color: 'orange',
      description: 'Deploy training programs across departments',
      defaultSteps: [
        { name: 'Training Needs Analysis', duration: 7, critical: true },
        { name: 'Content Development', duration: 21, critical: true },
        { name: 'Pilot Testing', duration: 7, critical: false },
        { name: 'Full Deployment', duration: 30, critical: true },
        { name: 'Progress Monitoring', duration: 60, critical: false },
        { name: 'Effectiveness Review', duration: 14, critical: true }
      ]
    },
    policy: {
      name: 'Policy Implementation',
      icon: FiSettings,
      color: 'gray',
      description: 'Roll out new policies or update existing ones',
      defaultSteps: [
        { name: 'Policy Development', duration: 14, critical: true },
        { name: 'Stakeholder Review', duration: 7, critical: true },
        { name: 'Legal Compliance Check', duration: 5, critical: true },
        { name: 'Communication Strategy', duration: 3, critical: false },
        { name: 'Staff Training & Rollout', duration: 21, critical: true },
        { name: 'Implementation Monitoring', duration: 30, critical: false }
      ]
    },
    compliance: {
      name: 'Compliance Audit',
      icon: FiBarChart3,
      color: 'red',
      description: 'Regular compliance audits and remediation',
      defaultSteps: [
        { name: 'Audit Scope Definition', duration: 3, critical: true },
        { name: 'Data Collection', duration: 10, critical: true },
        { name: 'Gap Analysis', duration: 7, critical: true },
        { name: 'Remediation Planning', duration: 5, critical: true },
        { name: 'Implementation Phase', duration: 30, critical: true },
        { name: 'Follow-up Review', duration: 14, critical: false }
      ]
    }
  };

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      setError(null);

      // NOTE: Workflows table doesn't have created_by column yet
      // All users in tenant can see all workflows for now
      const data = await workflowService.getAllWorkflows();
      setActiveWorkflows(data);
    } catch (err) {
      console.error('Error loading workflows:', err);
      setError('Failed to load workflows. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const createNewWorkflow = async (formData) => {
    try {
      setLoading(true);
      setError(null);

      const template = workflowTemplates[formData.type];
      
      const workflowData = {
        workflow_type: formData.type,
        workflow_name: formData.name,
        description: formData.description,
        department: formData.department,
        owner: formData.owner,
        priority: formData.priority,
        status: 'planning',
        start_date: formData.startDate,
        estimated_end_date: calculateEndDate(formData.startDate, template.defaultSteps),
        steps: template.defaultSteps.map((step, index) => ({
          name: step.name,
          duration: step.duration,
          critical: step.critical
        }))
      };

      await workflowService.createWorkflow(workflowData);
      
      // Reload workflows
      await loadWorkflows();
      setShowNewWorkflowForm(false);
    } catch (err) {
      console.error('Error creating workflow:', err);
      setError('Failed to create workflow. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateEndDate = (startDate, steps) => {
    const totalDuration = steps.reduce((sum, step) => sum + step.duration, 0);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + totalDuration);
    return endDate.toISOString();
  };

  const updateWorkflowStatus = async (workflowId, newStatus) => {
    try {
      setLoading(true);
      setError(null);

      await workflowService.updateWorkflow(workflowId, {
        status: newStatus
      });

      // Add audit entry
      await workflowService.addWorkflowAuditEntry(
        workflowId,
        'Status Changed',
        `Workflow status changed to ${newStatus}`
      );

      // Reload workflows
      await loadWorkflows();
    } catch (err) {
      console.error('Error updating workflow status:', err);
      setError('Failed to update workflow status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorkflow = async (workflow) => {
    setWorkflowToDelete(workflow);
    const dependencies = await checkWorkflowDependencies(workflow.id);
    setWorkflowDependencies(dependencies);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteWorkflow = async () => {
    try {
      setDeleteLoading(true);
      setError(null);

      await workflowService.deleteWorkflow(workflowToDelete.id);
      
      // Reload workflows
      await loadWorkflows();
      setShowDeleteConfirm(false);
      setWorkflowToDelete(null);
      setWorkflowDependencies({});
    } catch (err) {
      console.error('Error deleting workflow:', err);
      setError('Failed to delete workflow. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning': return 'bg-yellow-100 text-yellow-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'on-hold': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getWorkflowIcon = (type) => {
    return workflowTemplates[type]?.icon || FiSettings;
  };

  const getWorkflowColor = (type) => {
    const colorMap = {
      blue: 'text-blue-600',
      green: 'text-green-600',
      purple: 'text-purple-600',
      orange: 'text-orange-600',
      gray: 'text-gray-600',
      red: 'text-red-600'
    };
    return colorMap[workflowTemplates[type]?.color] || 'text-gray-600';
  };

  const filteredWorkflows = activeWorkflows.filter(workflow => {
    return filterStatus === 'all' || workflow.status === filterStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredWorkflows.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedWorkflows = filteredWorkflows.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus]);

  // Stats for page header
  const headerStats = [
    {
      label: 'Total Workflows',
      value: activeWorkflows.length,
      icon: FiSettings,
      iconColor: 'text-blue-600'
    },
    {
      label: 'Active',
      value: activeWorkflows.filter(w => w.status === 'active').length,
      icon: FiPlay,
      iconColor: 'text-blue-600'
    },
    {
      label: 'Planning',
      value: activeWorkflows.filter(w => w.status === 'planning').length,
      icon: FiClock,
      iconColor: 'text-yellow-600'
    },
    {
      label: 'Completed',
      value: activeWorkflows.filter(w => w.status === 'completed').length,
      icon: FiCheck,
      iconColor: 'text-green-600'
    }
  ];

  const headerActions = [
    {
      type: 'button',
      label: 'New Workflow',
      icon: FiPlus,
      variant: 'primary',
      onClick: () => setShowNewWorkflowForm(true)
    }
  ];

  if (loading && activeWorkflows.length === 0) {
    return (
      <>
        <PageHeader
          title="Company-Level Workflow Management"
          subtitle="Manage organizational processes and initiatives across departments"
          stats={headerStats}
          actions={headerActions}
        />
        <div className="max-w-7xl mx-auto p-6">
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600 font-medium">Loading workflows...</p>
          </div>
        </div>
      </>
    );
  }

  if (error && activeWorkflows.length === 0) {
    return (
      <>
        <PageHeader
          title="Company-Level Workflow Management"
          subtitle="Manage organizational processes and initiatives across departments"
          stats={headerStats}
          actions={headerActions}
        />
        <div className="max-w-7xl mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-4xl mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Data</h3>
            <p className="text-red-700 mb-4">{error}</p>
            <button
              onClick={loadWorkflows}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Company-Level Workflow Management"
        subtitle="Manage organizational processes and initiatives across departments"
        stats={headerStats}
        actions={headerActions}
      />

      <div className="max-w-7xl mx-auto p-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="on-hold">On Hold</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 flex items-center space-x-2 transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title="Grid View"
              >
                <SafeIcon icon={FiGrid} />
                <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 flex items-center space-x-2 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title="List View"
              >
                <SafeIcon icon={FiList} />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
          </div>
        </div>

        {/* Workflow Templates Overview */}
        {activeWorkflows.length === 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Available Workflow Templates</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(workflowTemplates).map(([key, template]) => (
                <div
                  key={key}
                  className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <SafeIcon
                      icon={template.icon}
                      className={`text-2xl ${getWorkflowColor(key)}`}
                    />
                    <h3 className="font-semibold text-gray-900">{template.name}</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{template.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">{template.defaultSteps.length} steps</span>
                    <button
                      onClick={() => {
                        setSelectedWorkflowType(key);
                        setShowNewWorkflowForm(true);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Create Workflow →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Workflows Grid View */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {paginatedWorkflows.map((workflow, index) => (
            <motion.div
              key={workflow.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <SafeIcon
                      icon={getWorkflowIcon(workflow.workflow_type)}
                      className={`text-2xl ${getWorkflowColor(workflow.workflow_type)}`}
                    />
                    <div>
                      <h3 className="font-bold text-lg text-gray-900 mb-1">
                        {workflow.workflow_name}
                      </h3>
                      <p className="text-sm text-gray-600">{workflow.workflow_id}</p>
                    </div>
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                      workflow.status
                    )}`}
                  >
                    {workflow.status.toUpperCase()}
                  </span>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiUsers} className="text-gray-500" />
                    <span className="text-sm">{workflow.department}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiUserCheck} className="text-gray-500" />
                    <span className="text-sm">{workflow.owner}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCalendar} className="text-gray-500" />
                      <span className="text-xs text-gray-500">
                        Start: {new Date(workflow.start_date).toLocaleDateString()}
                      </span>
                    </div>
                    <span
                      className={`text-xs font-medium ${
                        workflow.priority === 'high'
                          ? 'text-red-600'
                          : workflow.priority === 'medium'
                          ? 'text-yellow-600'
                          : 'text-green-600'
                      }`}
                    >
                      {workflow.priority?.toUpperCase()} PRIORITY
                    </span>
                  </div>
                </div>

                {/* Enhanced Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium text-gray-700">Progress</span>
                    <span className="text-xs text-gray-500">
                      {workflow.progress.completedSteps}/{workflow.progress.totalSteps} steps
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${workflow.progress.completionPercentage}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    {workflow.progress.completionPercentage}% complete
                  </div>
                </div>

                {/* Current Step */}
                <div className="mb-4">
                  <div className="text-xs font-medium text-gray-700 mb-1">Current Step</div>
                  <div className="text-sm font-medium text-blue-600">
                    {workflow.workflow_steps?.find(s => s.status === 'active')?.step_name ||
                      workflow.workflow_steps?.find(s => s.status === 'ready')?.step_name ||
                      'All steps completed'}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2">
                  <a
                    href={`#/workflow/${workflow.id}`}
                    className="flex-1 bg-blue-600 text-white text-center py-2 rounded hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    View Details
                  </a>
                  <div className="flex space-x-1">
                    {workflow.status === 'planning' && (
                      <button
                        onClick={() => updateWorkflowStatus(workflow.id, 'active')}
                        className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                        title="Start Workflow"
                      >
                        <SafeIcon icon={FiPlay} />
                      </button>
                    )}
                    {workflow.status === 'active' && (
                      <button
                        onClick={() => updateWorkflowStatus(workflow.id, 'on-hold')}
                        className="px-3 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                        title="Pause Workflow"
                      >
                        <SafeIcon icon={FiPause} />
                      </button>
                    )}
                    {workflow.status === 'on-hold' && (
                      <button
                        onClick={() => updateWorkflowStatus(workflow.id, 'active')}
                        className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        title="Resume Workflow"
                      >
                        <SafeIcon icon={FiRefreshCw} />
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteWorkflow(workflow)}
                        className="px-3 py-2 border border-red-300 text-red-600 rounded hover:bg-red-50 transition-colors"
                        title="Delete Workflow"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
            ))}
          </div>
        )}

        {/* Active Workflows List View */}
        {viewMode === 'list' && (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Workflow</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedWorkflows.map((workflow) => (
                  <motion.tr
                    key={workflow.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <button
                          onClick={() => window.location.hash = `/workflow/${workflow.id}`}
                          className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline text-left"
                        >
                          {workflow.workflow_name}
                        </button>
                        <div className="text-sm text-gray-500">{workflow.workflow_id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <SafeIcon
                          icon={getWorkflowIcon(workflow.workflow_type)}
                          className={`mr-2 ${getWorkflowColor(workflow.workflow_type)}`}
                        />
                        <span className="text-sm text-gray-900">
                          {workflowTemplates[workflow.workflow_type]?.name || workflow.workflow_type}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{workflow.owner}</div>
                      <div className="text-sm text-gray-500">{workflow.department}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(workflow.status)}`}>
                        {workflow.status.replace('-', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2 mr-2" style={{ width: '100px' }}>
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${workflow.progress?.completionPercentage || 0}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-900">{workflow.progress?.completionPercentage || 0}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        workflow.priority === 'high' ? 'bg-red-100 text-red-800' :
                        workflow.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {workflow.priority?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(workflow.start_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => window.location.hash = `/workflow/${workflow.id}`}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="View Details"
                        >
                          <SafeIcon icon={FiEye} className="w-5 h-5" />
                        </button>
                        {workflow.status !== 'completed' && workflow.status !== 'cancelled' && (
                          <>
                            {workflow.status === 'planning' && (
                              <button
                                onClick={() => updateWorkflowStatus(workflow.id, 'active')}
                                className="text-green-600 hover:text-green-900 p-1"
                                title="Start Workflow"
                              >
                                <SafeIcon icon={FiPlay} className="w-5 h-5" />
                              </button>
                            )}
                            {workflow.status === 'active' && (
                              <button
                                onClick={() => updateWorkflowStatus(workflow.id, 'on-hold')}
                                className="text-yellow-600 hover:text-yellow-900 p-1"
                                title="Pause Workflow"
                              >
                                <SafeIcon icon={FiPause} className="w-5 h-5" />
                              </button>
                            )}
                            {workflow.status === 'on-hold' && (
                              <button
                                onClick={() => updateWorkflowStatus(workflow.id, 'active')}
                                className="text-blue-600 hover:text-blue-900 p-1"
                                title="Resume Workflow"
                              >
                                <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
                              </button>
                            )}
                          </>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteWorkflow(workflow)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete Workflow"
                          >
                            <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredWorkflows.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-700">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredWorkflows.length)} of {filteredWorkflows.length} workflows
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
              >
                <SafeIcon icon={FiChevronLeft} />
                <span>Previous</span>
              </button>
              
              <div className="flex space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 rounded-lg ${
                      currentPage === page
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
              >
                <span>Next</span>
                <SafeIcon icon={FiChevronRight} />
              </button>
            </div>
          </div>
        )}

        {filteredWorkflows.length === 0 && filterStatus !== 'all' && (
          <div className="text-center py-12">
            <SafeIcon icon={FiSettings} className="text-6xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No workflows found</h3>
            <p className="text-gray-600 mb-4">No workflows match the selected status filter.</p>
          </div>
        )}

        {/* New Workflow Form Modal */}
        {showNewWorkflowForm && (
          <NewWorkflowModal
            workflowTemplates={workflowTemplates}
            selectedType={selectedWorkflowType}
            onSubmit={createNewWorkflow}
            onCancel={() => {
              setShowNewWorkflowForm(false);
              setSelectedWorkflowType('');
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteConfirm && workflowToDelete !== null}
          onClose={() => {
            setShowDeleteConfirm(false);
            setWorkflowToDelete(null);
            setWorkflowDependencies({});
          }}
          onConfirm={confirmDeleteWorkflow}
          itemType="Workflow"
          itemName={workflowToDelete?.workflow_name || ''}
          dependencies={workflowDependencies}
          loading={deleteLoading}
        />
      </div>
    </>
  );
};

// New Workflow Form Modal Component (keeping existing implementation)
const NewWorkflowModal = ({ workflowTemplates, selectedType, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    type: selectedType || '',
    name: '',
    description: '',
    department: '',
    owner: '',
    priority: 'medium',
    startDate: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.type && formData.name && formData.department && formData.owner) {
      onSubmit(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Company Workflow</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Workflow Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select Workflow Type</option>
              {Object.entries(workflowTemplates).map(([key, template]) => (
                <option key={key} value={key}>
                  {template.name}
                </option>
              ))}
            </select>
            {formData.type && (
              <p className="text-sm text-gray-600 mt-1">
                {workflowTemplates[formData.type].description}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Workflow Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Q1 2024 Marketing Recruitment"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Brief description of this workflow instance"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select
                value={formData.department}
                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Department</option>
                <option value="HR">Human Resources</option>
                <option value="Marketing">Marketing</option>
                <option value="IT">Information Technology</option>
                <option value="Finance">Finance</option>
                <option value="Operations">Operations</option>
                <option value="Sales">Sales</option>
                <option value="Customer Service">Customer Service</option>
                <option value="Production">Production</option>
                <option value="All Departments">All Departments</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Workflow Owner</label>
              <input
                type="text"
                value={formData.owner}
                onChange={(e) => setFormData(prev => ({ ...prev, owner: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Owner name"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {formData.type && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Workflow Steps Preview</h4>
              <div className="space-y-1">
                {workflowTemplates[formData.type].defaultSteps.map((step, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-blue-800">{step.name}</span>
                    <span className="text-blue-600">{step.duration} days</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Workflow
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CompanyWorkflows;